import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, AlertCircle, Euro } from "lucide-react";
import { useSEO, businessSchema } from "../lib/seo";
import { Reveal } from "../components/Reveal";
import { ContactForm } from "../components/ContactForm";
import { SITE, waLink } from "../lib/config";

export default function Contacto() {
  useSEO({
    title: "Contacto | Psicóloga sanitaria en Málaga – Victoria Fuentes",
    description:
      "Contacta con Victoria Fuentes, psicóloga sanitaria en Málaga. Atención psicológica presencial en el centro de Málaga y online, con cita previa.",
    path: "/contacto",
    jsonLd: businessSchema,
  });

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(SITE.mapsQuery)}&output=embed`;

  return (
    <div data-testid="contacto-page">
      <section className="mx-auto max-w-7xl px-6 pb-12 pt-36 md:px-12 md:pt-44">
        <Reveal>
          <p className="overline">Contacto</p>
          <h1 className="mt-5 max-w-3xl font-serif text-5xl font-light leading-[1.05] tracking-tight text-ink md:text-7xl">
            Contacta con una psicóloga sanitaria en Málaga
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-soft">
            Cuéntame qué necesitas y te responderé lo antes posible. Puedes escribirme mediante el formulario,
            por WhatsApp o llamarme directamente. Atiendo de forma presencial en Málaga y también en modalidad
            online, siempre con cita previa.
          </p>
          <p className="mt-6 font-serif text-xl italic text-sage-dark md:text-2xl">
            Pedir ayuda no es una debilidad, es una forma de cuidarte.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-6 pb-24 md:grid-cols-12 md:px-12 md:pb-32">
        <div className="md:col-span-7">
          <Reveal>
            <div className="rounded-4xl border border-ink/10 bg-bg-secondary p-6 md:p-10">
              <h2 className="font-serif text-2xl text-ink">Escríbeme</h2>
              <p className="mt-2 text-sm text-ink-soft">Todos los campos son necesarios para poder atenderte.</p>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">Puedes contarme brevemente qué te preocupa y me pondré en contacto contigo para informarte sobre cómo puedo ayudarte.</p>
              <div className="mt-6">
                <ContactForm testId="main-contact-form" subject="Solicitud de primera cita" />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-5 md:pl-4">
          <Reveal delay={0.1}>
            <div className="space-y-4">
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 rounded-3xl bg-sage px-6 py-5 text-white transition-transform duration-300 hover:scale-[1.02]">
                <MessageCircle className="h-6 w-6" strokeWidth={1.5} />
                <div>
                  <p className="text-sm opacity-80">Respuesta rápida</p>
                  <p className="text-lg font-medium">Escríbeme por WhatsApp</p>
                </div>
              </a>

              <div className="rounded-3xl border border-ink/10 bg-bg-primary p-6">
                <a href={`tel:+34${SITE.phone}`} className="flex items-center gap-4 text-ink transition-colors hover:text-sage-dark">
                  <Phone className="h-5 w-5 text-sage-dark" strokeWidth={1.5} />
                  <div><p className="text-xs text-ink-muted">Teléfono</p><p className="text-lg">{SITE.phoneDisplay}</p></div>
                </a>
                <div className="my-4 h-px bg-ink/10" />
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 text-ink transition-colors hover:text-sage-dark">
                  <Mail className="h-5 w-5 text-sage-dark" strokeWidth={1.5} />
                  <div><p className="text-xs text-ink-muted">Email</p><p className="break-all text-base">{SITE.email}</p></div>
                </a>
                <div className="my-4 h-px bg-ink/10" />
                <div className="flex items-center gap-4 text-ink">
                  <MapPin className="h-5 w-5 text-sage-dark" strokeWidth={1.5} />
                  <div><p className="text-xs text-ink-muted">Consulta</p><p className="text-base">{SITE.address}, {SITE.city}</p></div>
                </div>
                <div className="my-4 h-px bg-ink/10" />
                <div className="flex items-center gap-4 text-ink">
                  <Clock className="h-5 w-5 text-sage-dark" strokeWidth={1.5} />
                  <div><p className="text-xs text-ink-muted">Horario</p><p className="text-base">{SITE.schedule}</p></div>
                </div>
                <div className="my-4 h-px bg-ink/10" />
                <div className="flex items-center gap-4 text-ink" data-testid="contact-honorarios">
                  <Euro className="h-5 w-5 text-sage-dark" strokeWidth={1.5} />
                  <div><p className="text-xs text-ink-muted">Honorarios</p><p className="text-base">Sesión individual: 60 €</p><p className="text-sm text-ink-soft">Duración aproximada: 60 minutos.</p></div>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-3xl bg-bg-sage p-5" data-testid="cita-previa-notice">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1.5} />
                <p className="text-sm text-ink">Atención únicamente con cita previa. Sesiones presenciales en Málaga y online.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32">
        <Reveal>
          <div className="overflow-hidden rounded-4xl border border-ink/10">
            <iframe
              title="Ubicación de la consulta en Málaga"
              src={mapSrc}
              width="100%"
              height="440"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </div>
          <p className="mt-4 text-sm text-ink-muted">Atención presencial en consulta, con cita previa.</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-24 md:pb-32" data-testid="contacto-seo-local">
        <Reveal>
          <h2 className="font-serif text-2xl text-ink md:text-3xl">Consulta de psicología en Málaga</h2>
          <p className="mt-5 text-base leading-relaxed text-ink-soft">
            Mi consulta de psicología se encuentra en el centro de Málaga, en C/ Eslava 14. Ofrezco atención
            psicológica presencial para adultos, <Link to="/psicologa-infantil-malaga" className="text-sage-dark underline underline-offset-2 transition-colors hover:text-ink">adolescentes y niños</Link> a
            partir de los 6 años, así como sesiones online. Si estás buscando una psicóloga sanitaria en Málaga
            y quieres conocer cómo puedo ayudarte, puedes contactar conmigo para valorar tu caso y solicitar una
            primera cita.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
